/**
 * Token de acesso público (ADR-0011). As rotas de dados do portal exigem o cabeçalho
 * X-Public-Access quando a API do tenant liga PublicAccess:Enabled. O token sai de
 * POST /PublicAccess/Token depois do Cloudflare Turnstile e fica só em memória.
 *
 * Sem REACT_APP_TURNSTILE_SITE_KEY no build, nada disto roda: nenhum script externo, nenhum
 * cabeçalho. Com a site key e a API desligada, o Token responde 404 e o gate fica desligado
 * até recarregar a página.
 */
import axios, { type AxiosError, type InternalAxiosRequestConfig } from "axios";

export const PUBLIC_ACCESS_HEADER = "X-Public-Access";
export const PUBLIC_ACCESS_FORM_FIELD = "PublicAccess";

const SITE_KEY = (import.meta.env.REACT_APP_TURNSTILE_SITE_KEY as string | undefined)?.trim() || "";
const SCRIPT_URL = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
const RENEW_MARGIN_MS = 60_000;

// Espelha os controllers com [PublicAccessRequired] na API.
const PROTECTED_ROUTES = [
  /^\/?PublicLote\//i,
  /^\/?PublicImovel\//i,
  /^\/?PublicLogradouro\//i,
  /^\/?PublicArquivoDigitalizado\//i,
  /^\/?PublicArquivoGeralFinancas\//i,
  /^\/?PublicPontoTematico\//i,
  /^\/?PhotoView\//i,
];

interface Turnstile {
  render(container: HTMLElement, options: Record<string, unknown>): string;
  remove(widgetId: string): void;
}

declare global {
  interface Window {
    turnstile?: Turnstile;
  }
}

type GateState =
  | { kind: "off" }
  | { kind: "empty" }
  | { kind: "ready"; token: string; expiresAt: number };

let state: GateState = SITE_KEY ? { kind: "empty" } : { kind: "off" };
let pending: Promise<string | null> | null = null;
let scriptLoad: Promise<Turnstile> | null = null;

// Instância sem interceptadores: a emissão não pode disparar outra emissão.
const issuer = axios.create();

export function isProtectedRoute(url: string | undefined): boolean {
  if (!url) return false;
  const base = axios.defaults.baseURL ?? "";
  const path = base && url.startsWith(base) ? url.slice(base.length) : url;
  return PROTECTED_ROUTES.some((rx) => rx.test(path));
}

function loadTurnstile(): Promise<Turnstile> {
  if (window.turnstile) return Promise.resolve(window.turnstile);
  scriptLoad ??= new Promise<Turnstile>((resolve, reject) => {
    const script = document.createElement("script");
    script.src = SCRIPT_URL;
    script.async = true;
    script.onload = () => (window.turnstile ? resolve(window.turnstile) : reject(new Error("Turnstile não carregou")));
    script.onerror = () => {
      scriptLoad = null;
      reject(new Error("Turnstile não carregou"));
    };
    document.head.appendChild(script);
  });
  return scriptLoad;
}

/** Roda o widget no modo gerenciado; só aparece (canto inferior direito) se a Cloudflare pedir interação. */
async function solveChallenge(): Promise<string> {
  const turnstile = await loadTurnstile();
  const container = document.createElement("div");
  container.className = "public-access-challenge";
  Object.assign(container.style, { position: "fixed", right: "16px", bottom: "16px", zIndex: "10000" });
  document.body.appendChild(container);

  try {
    return await new Promise<string>((resolve, reject) => {
      const widgetId = turnstile.render(container, {
        sitekey: SITE_KEY,
        appearance: "interaction-only",
        callback: (token: string) => resolve(token),
        "error-callback": () => reject(new Error("Turnstile recusou o desafio")),
        "expired-callback": () => reject(new Error("Desafio do Turnstile expirou")),
      });
      container.dataset.widgetId = widgetId;
    });
  } finally {
    if (container.dataset.widgetId) turnstile.remove(container.dataset.widgetId);
    container.remove();
  }
}

async function issueToken(): Promise<string | null> {
  const turnstileToken = await solveChallenge();
  try {
    const { data } = await issuer.post<{ token: string; expiresAt: string }>(
      "/PublicAccess/Token",
      { turnstileToken },
      { baseURL: axios.defaults.baseURL },
    );
    state = { kind: "ready", token: data.token, expiresAt: Date.parse(data.expiresAt) };
    return data.token;
  } catch (error) {
    if ((error as AxiosError).response?.status === 404) {
      state = { kind: "off" };
      return null;
    }
    throw error;
  }
}

/** Token válido em memória, ou uma emissão nova compartilhada por todas as chamadas que chegarem juntas. */
export function getPublicAccessToken(): Promise<string | null> {
  if (state.kind === "off") return Promise.resolve(null);
  if (state.kind === "ready" && state.expiresAt - RENEW_MARGIN_MS > Date.now()) return Promise.resolve(state.token);

  pending ??= issueToken().finally(() => {
    pending = null;
  });
  return pending;
}

function discardToken(token: string) {
  if (state.kind === "ready" && state.token === token) state = { kind: "empty" };
}

type GatedConfig = InternalAxiosRequestConfig & { _publicAccessToken?: string; _publicAccessRetried?: boolean };

async function attachToken(config: GatedConfig): Promise<GatedConfig> {
  if (!isProtectedRoute(config.url)) return config;
  const token = await getPublicAccessToken();
  if (token) {
    config.headers.set(PUBLIC_ACCESS_HEADER, token);
    config._publicAccessToken = token;
  }
  return config;
}

async function retryOnceOn401(error: AxiosError) {
  const config = error.config as GatedConfig | undefined;
  const data = error.response?.data as { code?: string } | undefined;
  if (!config || error.response?.status !== 401 || data?.code !== "public-access-required" || config._publicAccessRetried) {
    throw error;
  }

  if (config._publicAccessToken) discardToken(config._publicAccessToken);
  config._publicAccessRetried = true;
  return axios(config);
}

export function installPublicAccess() {
  if (state.kind === "off") return;
  axios.interceptors.request.use(attachToken);
  axios.interceptors.response.use((r) => r, retryOnceOn401);
}
